import { apiClient } from "../lib/api-client";
import { User, ApiResponse } from "../types/api.types";

export interface SignupData {
  email: string;
  password: string;
  name: string;
  otp: string;
}

export interface SigninData {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

const getReferralCookie = (): string | undefined => {
  const name = "vidlaya_referral=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return undefined;
};

export const authService = {
  async signup(data: SignupData): Promise<AuthResponse> {
    const referralSource = getReferralCookie();
    const response = await apiClient.post<AuthResponse>("/auth/signup", { ...data, referralSource });
    if (response.success && response.data) {
      apiClient.setToken(response.data.token);
      localStorage.setItem('user_role', (response.data.user as any).role || 'user');
      return response.data;
    }
    throw new Error(response.message || "Signup failed");
  },

  async sendOtp(email: string): Promise<void> {
    const response = await apiClient.post<void>("/auth/send-otp", { email });
    if (!response.success) {
      throw new Error(response.message || "Failed to send OTP");
    }
  },

  async signin(data: SigninData): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>("/auth/signin", data);
    if (response.success && response.data) {
      apiClient.setToken(response.data.token);
      localStorage.setItem('user_role', (response.data.user as any).role || 'user');
      return response.data;
    }
    throw new Error(response.message || "Signin failed");
  },

  async googleSignin(credential: string): Promise<AuthResponse> {
    const referralSource = getReferralCookie();
    const response = await apiClient.post<AuthResponse>("/auth/google", { credential, referralSource });
    if (response.success && response.data) {
      apiClient.setToken(response.data.token);
      localStorage.setItem('user_role', (response.data.user as any).role || 'user');
      return response.data;
    }
    throw new Error(response.message || "Google sign-in failed");
  },

  async getProfile(): Promise<User> {
    const response = await apiClient.get<{ user: User }>("/auth/profile");
    if (response.success && response.data) {
      localStorage.setItem('user_role', (response.data.user as any).role || 'user');
      return response.data.user;
    }
    throw new Error(response.message || "Failed to get profile");
  },

  async updateProfile(data: { name: string }): Promise<User> {
    const response = await apiClient.patch<{ user: User }>("/auth/profile", data);
    if (response.success && response.data) {
      return response.data.user;
    }
    throw new Error(response.message || "Failed to update profile");
  },

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<void> {
    const response = await apiClient.put<void>("/auth/change-password", data);
    if (!response.success) {
      throw new Error(response.message || "Failed to change password");
    }
  },

  async deleteAccount(password?: string): Promise<void> {
    const response = await apiClient.delete<void>("/auth/account", { data: { password } });
    if (!response.success) {
      throw new Error(response.message || "Failed to delete account");
    }
  },

  logout() {
    apiClient.clearToken();
    localStorage.removeItem('user_role');
  },

  isAuthenticated(): boolean {
    return !!apiClient.getToken();
  },
};
